<script setup lang="ts">
import type { ProductLine } from '~/types/api'

const props = defineProps<{
  setNid?: number
  line?: ProductLine | null
}>()

const itemsPerPage = ref(3)
const page = ref(1)
const productSetsRepository = createProductSetsRepository()

const { data: references, status } = await useAsyncData(
  () =>
    productSetsRepository.getAllReferencesPagination(
      page.value,
      itemsPerPage.value,
      props.setNid
    ),
  {
    watch: [page, itemsPerPage],
    default: defaultPaginationObject,
  }
)

const open = ref(false)

const referenceElement = ref<HTMLElement | null>()
function toggleOpen() {
  if (!open.value) {
    open.value = true
    itemsPerPage.value = 12
  } else {
    referenceElement.value?.scrollIntoView({ behavior: 'smooth' })
    setTimeout(() => {
      itemsPerPage.value = 3
      open.value = false
    }, 500)
  }
}

watch(
  () => page.value,
  () => {
    referenceElement.value?.scrollIntoView({ behavior: 'smooth' })
  }
)
</script>

<template>
  <UiSection
    v-if="references.items.length"
    gray
    :title="$t('base.references.setsReferences')"
  >
    <div
      ref="referenceElement"
      class="grid scroll-m-20 grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3"
    >
      <ReferencesCard
        v-for="reference in references.items"
        :key="reference.nid"
        class="!w-full"
        :reference="reference"
      />
    </div>
    <div class="flex justify-end pr-6 pt-5">
      <UiPagination
        v-show="open"
        v-model:value="page"
        :items-per-page="itemsPerPage"
        :item-count="references.itemCount"
        :loading="status === 'pending'"
      />
    </div>
    <div
      v-if="references.items.length"
      class="flex w-full justify-center pt-16"
    >
      <div class="cursor-pointer pb-1" @click="toggleOpen">
        <template v-if="!open">
          <UiAnimatedUnderlineText>
            {{ $t('base.references.allSetsReferences', [line?.title]) }}
          </UiAnimatedUnderlineText>
        </template>
        <template v-else>
          <UiAnimatedUnderlineText>
            {{ $t('base.references.hideReferences', [line?.title]) }}
          </UiAnimatedUnderlineText>
        </template>
      </div>
    </div>
  </UiSection>
</template>
